import { accessOptionToSortOrder, accessTypeToDefaultAccessConfig } from '../static/constants/accesses';
import { AccessConfig, UserConfig } from '../types/user';
import { isFokusUser } from './userConfigUtils';


export const userHasAccessToPlatform = (userConfig: UserConfig | null) => {
  return userConfig && userConfig.accessConfig.accessType !== 'none';
};


export const userHasMobileAccess = (userConfig: UserConfig | null) => {
  return userConfig && isFokusUser(userConfig);
};


export const userHasMaintenanceAccess = (userConfig: UserConfig | null) => {
  return userConfig && isFokusUser(userConfig);
};


// we may only store a subset of the access config in the firestore
// this function returns the complete access config, where properties in the given config have presedence, and other properties are filled based on access type
export const getCompleteAccessConfig = (accessConfig: Partial<AccessConfig> | undefined): AccessConfig => {

  // access config should exist for all users
  const accessType = accessConfig?.accessType ?? 'scout';

  return {
    ...accessTypeToDefaultAccessConfig[accessType],
    ...accessConfig,
  };
};

// we prefer to only store the part of the access config that differ from the default config for the given access type
export const getAccessConfigToStore = (accessConfig: AccessConfig): Partial<AccessConfig> => {
  const accessConfigToStore: Partial<AccessConfig> = {
    accessType: accessConfig.accessType,
  };

  const defaultAccessConfig = accessTypeToDefaultAccessConfig[accessConfig.accessType];

  Object.entries(accessConfig).forEach(([key, value]) => {
    if (value !== defaultAccessConfig[key as keyof AccessConfig]) {
      accessConfigToStore[key as keyof AccessConfig] = value;
    }
  });

  return accessConfigToStore;
};


export const userHasAccessToTeam = (
  userConfig: UserConfig,
  teamId: string,
  teamIsAcademyTeam: boolean,
  teamOwner: string | null
) => {

  if (teamId === 'ownTeam') {
    return true;
  }

  if (teamIsAcademyTeam) {
    return userConfig.accessConfig.hasAccessToAcademy;
  }

  if (userConfig.accessConfig.logisticTeamAccess === 'personal') {
    return userConfig.email !== undefined && userConfig.email === teamOwner;
  }

  return userConfig.accessConfig.logisticTeamAccess === 'full';
};


// get sort distance between two accesses
export const getSortDistanceAccess = (a: UserConfig, b: UserConfig) => {
  const aHasAccess = a.accessConfig.accessType && accessOptionToSortOrder[a.accessConfig.accessType];
  const bHasAccess = b.accessConfig.accessType && accessOptionToSortOrder[b.accessConfig.accessType];

  if (!bHasAccess && aHasAccess) return -1;
  if (!aHasAccess && bHasAccess) return 1;

  if (aHasAccess && bHasAccess) {
    return accessOptionToSortOrder[b.accessConfig.accessType] - accessOptionToSortOrder[a.accessConfig.accessType];
  }

  return (a.name ?? '').localeCompare(b.name ?? '');
};


export const accessConfigsAreEqual = (
  a: AccessConfig | undefined,
  b: AccessConfig | undefined,
) => {
  if (!a && !b) return true;
  if (!a || !b) return false;

  return (
    a.accessType === b.accessType

    && a.logisticTeamAccess === b.logisticTeamAccess
    && a.hasAccessToScouting === b.hasAccessToScouting
    && a.reportAccess === b.reportAccess

    && a.hasAccessToModifyOwnTeam === b.hasAccessToModifyOwnTeam
    && a.hasAccessToViewTeamRoles === b.hasAccessToViewTeamRoles
    && a.hasAccessToEconomy === b.hasAccessToEconomy

    && a.hasAccessToPlanner === b.hasAccessToPlanner
    && a.hasAccessToAcademy === b.hasAccessToAcademy
    && a.hasAccessToManageRoles === b.hasAccessToManageRoles
    && a.hasAccessToManageFormations === b.hasAccessToManageFormations
  );
};
